import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DateInputFilter } from "@/components/base/date-input-filter";
import { formatDate } from "@/utils/date";

// ─── Private icons ────────────────────────────────────────────────────────────

function IconSort() {
    return (
        <svg
            className="h-3.5 w-3.5 opacity-50"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="m21 16-4 4-4-4" />
            <path d="M17 20V4" />
            <path d="m3 8 4-4 4 4" />
            <path d="M7 4v16" />
        </svg>
    );
}

function IconFilter() {
    return (
        <svg
            className="h-3 w-3"
            data-testid="filter-indicator"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <path d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z" />
        </svg>
    );
}

function IconX() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
    );
}

// ─── Sort ─────────────────────────────────────────────────────────────────────

export type SortDirection = "asc" | "desc";

export function sortableHeaderClass(isActive: boolean): string {
    return `flex items-center gap-1 transition ${isActive ? "text-blue-600" : "text-gray-500 hover:text-gray-700"}`;
}

export function SortIndicator({ direction }: { direction?: SortDirection }) {
    if (!direction) return <IconSort />;
    if (direction === "asc") {
        return (
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="m5 12 7-7 7 7" />
                <path d="M12 19V5" />
            </svg>
        );
    }
    return (
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14" />
            <path d="m19 12-7 7-7-7" />
        </svg>
    );
}

export function FilterIndicator() {
    return <IconFilter />;
}

// ─── Column Filter Popover ────────────────────────────────────────────────────

const POPOVER_WIDTH = 288;
const POPOVER_GUTTER = 16;

export function ColumnFilterPopover({
    isOpen,
    anchorEl,
    columnLabel,
    hint,
    inputType = "text",
    options,
    searchValue,
    savedValues = [],
    onSearchChange,
    onClose,
    onApply,
    onClear,
    onSelectValue,
    onRemoveSavedValue,
}: {
    isOpen: boolean;
    anchorEl: HTMLElement | null;
    columnLabel: string;
    hint?: string;
    inputType?: "text" | "date";
    options?: Array<{ label: string; value: string }>;
    searchValue: string;
    savedValues?: string[];
    onSearchChange: (value: string) => void;
    onClose: () => void;
    onApply?: (value?: string) => void;
    onClear?: () => void;
    onSelectValue?: (value: string) => void;
    onRemoveSavedValue?: (value: string) => void;
}) {
    const [position, setPosition] = useState({ top: 0, left: 0, width: POPOVER_WIDTH });
    const popoverRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen || !anchorEl) return;
        const update = () => {
            const rect = anchorEl.getBoundingClientRect();
            const vw = window.innerWidth || document.documentElement.clientWidth;
            const width = Math.min(POPOVER_WIDTH, Math.max(0, vw - POPOVER_GUTTER * 2));
            const maxLeft = Math.max(POPOVER_GUTTER, vw - width - POPOVER_GUTTER);
            const left = Math.min(Math.max(rect.left, POPOVER_GUTTER), maxLeft);
            setPosition({ top: rect.bottom + 8, left, width });
        };
        update();
        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, true);
        return () => {
            window.removeEventListener("resize", update);
            window.removeEventListener("scroll", update, true);
        };
    }, [isOpen, anchorEl]);

    useEffect(() => {
        if (!isOpen) return;
        const handlePointerDown = (e: PointerEvent) => {
            const target = e.target as Node;
            if (popoverRef.current && !popoverRef.current.contains(target) && !anchorEl?.contains(target)) {
                onClose();
            }
        };
        document.addEventListener("pointerdown", handlePointerDown);
        return () => document.removeEventListener("pointerdown", handlePointerDown);
    }, [isOpen, anchorEl, onClose]);

    if (!isOpen || !anchorEl || typeof document === "undefined") return null;

    return createPortal(
        <div
            ref={popoverRef}
            className="fixed z-[120] rounded-lg border border-gray-300 bg-white shadow-lg"
            style={{ top: position.top, left: position.left, width: position.width }}
        >
            {options ? (
                <div className="py-2">
                    {options.map((opt) => (
                        <label key={opt.value} className="flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                            <input
                                type="radio"
                                name="column-filter"
                                value={opt.value}
                                checked={searchValue === opt.value}
                                onChange={() => onSearchChange(opt.value)}
                                className="h-4 w-4 accent-blue-600"
                            />
                            {opt.label}
                        </label>
                    ))}
                </div>
            ) : (
                <>
                    <div className="p-3">
                        {inputType === "date" ? (
                            <DateInputFilter
                                value={searchValue || null}
                                onChange={(isoValue) => {
                                    onSearchChange(isoValue || "");
                                }}
                                ariaLabel={`Search by ${columnLabel.toLowerCase()}`}
                                autoFocus
                            />
                        ) : (
                            <input
                                autoFocus
                                type="text"
                                value={searchValue}
                                onChange={(e) => onSearchChange(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Escape") onClose();
                                    if (e.key === "Enter" && searchValue.trim() && onApply) {
                                        e.preventDefault();
                                        onApply();
                                    }
                                }}
                                placeholder={`Search ${columnLabel.toLowerCase()}...`}
                                className="w-full rounded-md border border-blue-500 px-3 py-2 text-sm ring-0 outline-none placeholder:text-gray-400 focus:border-blue-500"
                                aria-label={`Search ${columnLabel}`}
                            />
                        )}
                        {hint && <p className="mt-1.5 text-xs text-gray-400">{hint}</p>}
                    </div>
                    <div className="max-h-40 overflow-y-auto border-t border-gray-200 py-1">
                        {savedValues.length > 0 ? (
                            savedValues.map((value) => {
                                const displayValue = inputType === "date" ? formatDate(value) : value;
                                return (
                                    <div key={value} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100">
                                        <button
                                            type="button"
                                            className="min-w-0 flex-1 truncate rounded px-1 py-1 text-left text-sm text-gray-700 hover:text-blue-600"
                                            title={displayValue}
                                            onClick={() => {
                                                if (onSelectValue) {
                                                    onSelectValue(value);
                                                } else {
                                                    // Populate the input
                                                    onSearchChange(value);
                                                    // If an immediate apply handler is available, trigger it with
                                                    // the selected value so parents don't rely on stale state.
                                                    if (onApply) onApply(value);
                                                }
                                            }}
                                        >
                                            {displayValue}
                                        </button>
                                        <button
                                            type="button"
                                            className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                                            aria-label={`Remove saved search ${value}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onRemoveSavedValue?.(value);
                                            }}
                                        >
                                            <IconX />
                                        </button>
                                    </div>
                                );
                            })
                        ) : (
                            <p className="px-3 py-2 text-sm text-gray-400">No saved searches</p>
                        )}
                    </div>
                </>
            )}
            <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-3 py-2">
                <button
                    type="button"
                    disabled={!onClear}
                    className={
                        onClear
                            ? "rounded px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100"
                            : "cursor-not-allowed rounded px-2 py-1 text-xs font-medium text-gray-400"
                    }
                    title={onClear ? undefined : "Filtering not yet supported"}
                    onClick={() => onClear?.()}
                >
                    Clear
                </button>
                <button
                    type="button"
                    disabled={!onApply}
                    className={
                        onApply
                            ? "rounded bg-blue-600 px-2 py-1 text-xs font-semibold text-white hover:bg-blue-700"
                            : "cursor-not-allowed rounded bg-blue-600 px-2 py-1 text-xs font-semibold text-white opacity-50"
                    }
                    title={onApply ? undefined : "Filtering not yet supported"}
                    onClick={() => onApply?.(searchValue)}
                >
                    Apply
                </button>
            </div>
        </div>,
        document.body,
    );
}
